import domready from 'domready';
import {AdyenCheckout, ApplePay, GooglePay, PayPal} from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';

const getExpressCheckoutContainer = (selector) => document.querySelector(selector);

const clearExpressCheckoutContainer = (container) => {
    if (container) {
        if (container.adyenComponent) {
            container.adyenComponent.unmount();
            container.adyenComponent = null;
        }
        container.innerHTML = '';
    }
};

const isCountryAllowedForExpressCheckout = (countryCode) => window.expressCheckoutConfiguration.allowedCountryCodes
    .includes(countryCode);

const redirectToResultPage = (resultCode) => {
    const redirectPath = `${window.expressCheckoutConfiguration.resultPath}?resultCode=${resultCode}`;
    window.location.href = redirectPath;
};

/**
 * @returns {import('@adyen/adyen-web').CoreConfiguration}
 */
const createAdyenConfiguration = (expressCheckoutContext) => ({
    paymentMethodsResponse: window.expressCheckoutConfiguration.paymentMethodResponse,
    clientKey: window.expressCheckoutConfiguration.clientKey,
    locale: window.expressCheckoutConfiguration.locale,
    countryCode: window.expressCheckoutConfiguration.countryCode,
    environment: window.expressCheckoutConfiguration.environment,
    onSubmit: async (state, component, actions) => {
        try {
            const response = await fetch(window.expressCheckoutConfiguration.paymentPath, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    adyenStateData: state.data,
                    productId: window.expressCheckoutConfiguration.productId,
                    googlePayCustomerData: expressCheckoutContext.googlePayCustomerData,
                    applePayCustomerData: expressCheckoutContext.applePayCustomerData,
                    payPalCustomerData: expressCheckoutContext.payPalCustomerData
                })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            expressCheckoutContext.googlePayCustomerData = null;
            expressCheckoutContext.applePayCustomerData = null;
            expressCheckoutContext.payPalCustomerData = null;

            if (!result.resultCode) {
                actions.reject();
                return;
            }

            actions.resolve({
                resultCode: result.resultCode,
                action: result.action,
                order: result.order,
                donationToken: result.donationToken
            });
        } catch (error) {
            console.error('Express checkout onSubmit error:', error);
            actions.reject();
        }
    },
    onAdditionalDetails: async (state, component, actions) => {
        try {
            const response = await fetch(window.expressCheckoutConfiguration.additionalDetailsPath, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    adyenStateData: state.data,
                    payPalCustomerData: expressCheckoutContext.payPalCustomerData
                })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();

            expressCheckoutContext.googlePayCustomerData = null;
            expressCheckoutContext.applePayCustomerData = null;
            expressCheckoutContext.payPalCustomerData = null;

            if (!result.resultCode) {
                actions.reject();
                return;
            }

            actions.resolve({
                resultCode: result.resultCode,
                action: result.action,
                order: result.order,
                donationToken: result.donationToken
            });
        } catch (error) {
            console.error('Express checkout onAdditionalDetails error:', error);
            actions.reject();
        }
    },
    onPaymentCompleted: (result) => {
        redirectToResultPage(result.resultCode);
    },
    onPaymentFailed: (result, component) => {
        console.error('Express checkout onPaymentFailed', result, component);
    },
    onError: (error, component) => {
        console.error('Express checkout onError', error.name, error.message, error.stack, component);
    }
});

/**
 * @returns {import('@adyen/adyen-web').GooglePayConfiguration}
 */
const createGooglePayConfiguration = (expressCheckoutContext) => ({
    amount: {
        value: window.expressCheckoutConfiguration.amount,
        currency: window.expressCheckoutConfiguration.currency
    },
    countryCode: window.expressCheckoutConfiguration.countryCode,
    environment: window.expressCheckoutConfiguration.environment,
    emailRequired: true,
    allowedCardNetworks: ['MASTERCARD', 'VISA'],
    shippingAddressRequired: true,
    shippingAddressParameters: {
        allowedCountryCodes: window.expressCheckoutConfiguration.allowedCountryCodes,
        phoneNumberRequired: true
    },
    callbackIntents: ['SHIPPING_ADDRESS', 'PAYMENT_AUTHORIZATION'],
    paymentDataCallbacks: {
        onPaymentDataChanged() {
            return Promise.resolve({});
        }
    },
    isExpress: true,
    onAuthorized: (data, actions) => {
        const authorizedEvent = data.authorizedEvent ?? {};
        const shippingAddress = authorizedEvent.shippingAddress ?? {};

        expressCheckoutContext.googlePayCustomerData = {
            email: authorizedEvent.email,
            name: shippingAddress.name,
            address1: shippingAddress.address1,
            address2: shippingAddress.address2,
            address3: shippingAddress.address3,
            countryCode: shippingAddress.countryCode,
            locality: shippingAddress.locality,
            postalCode: shippingAddress.postalCode,
            phoneNumber: shippingAddress.phoneNumber,
            administrativeArea: shippingAddress.administrativeArea
        };
        expressCheckoutContext.applePayCustomerData = null;
        expressCheckoutContext.payPalCustomerData = null;

        actions.resolve();
    }
});

/**
 * @returns {import('@adyen/adyen-web').ApplePayConfiguration}
 */
const createApplePayConfiguration = (expressCheckoutContext) => ({
    amount: {
        value: window.expressCheckoutConfiguration.amount,
        currency: window.expressCheckoutConfiguration.currency
    },
    countryCode: window.expressCheckoutConfiguration.countryCode,
    merchantCapabilities: ['supports3DS'],
    supportedNetworks: ['visa', 'masterCard'],
    isExpress: true,
    requiredBillingContactFields: ['postalAddress'],
    requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
    onShippingContactSelected: (resolve, reject, event) => {
        const countryCode = event?.shippingContact?.countryCode ?? '';

        const shippingContactUpdate = {
            newTotal: {
                label: 'Elli',
                amount: (window.expressCheckoutConfiguration.amount / 100).toFixed(2),
                type: 'final'
            }
        };

        if (!isCountryAllowedForExpressCheckout(countryCode)) {
            shippingContactUpdate.errors = [
                new ApplePayError('shippingContactInvalid', 'countryCode', 'Cannot ship to the selected address')
            ];
            return resolve(shippingContactUpdate);
        }

        return resolve(shippingContactUpdate);
    },
    onAuthorized: (data, actions) => {
        const authorizedEvent = data.authorizedEvent ?? {};
        const shippingContact = authorizedEvent.payment?.shippingContact ?? {};

        expressCheckoutContext.applePayCustomerData = {
            emailAddress: shippingContact.emailAddress,
            givenName: shippingContact.givenName,
            familyName: shippingContact.familyName,
            addressLines: shippingContact.addressLines,
            countryCode: shippingContact.countryCode,
            locality: shippingContact.locality,
            postalCode: shippingContact.postalCode,
            phoneNumber: shippingContact.phoneNumber,
            administrativeArea: shippingContact.administrativeArea ?? ''
        };
        expressCheckoutContext.googlePayCustomerData = null;
        expressCheckoutContext.payPalCustomerData = null;

        actions.resolve();
    }
});

/**
 * @returns {import('@adyen/adyen-web').PayPalConfiguration}
 */
const createPayPalConfiguration = (expressCheckoutContext) => ({
    amount: {
        value: window.expressCheckoutConfiguration.amount,
        currency: window.expressCheckoutConfiguration.currency
    },
    isExpress: true,
    intent: 'authorize',
    blockPayPalPayLaterButton: true,
    blockPayPalCreditButton: true,
    blockPayPalVenmoButton: true,
    style: {
        height: 40
    },
    onShippingAddressChange: (data, actions) => {
        const countryCode = data?.shippingAddress?.countryCode ?? '';
        if (!isCountryAllowedForExpressCheckout(countryCode)) {
            return actions.reject('COUNTRY_ERROR');
        }

        return Promise.resolve();
    },
    onAuthorized: (data, actions) => {
        const authorizedEvent = data.authorizedEvent ?? {};
        const givenName = authorizedEvent?.payer?.name?.given_name ?? '';
        const surname = authorizedEvent?.payer?.name?.surname ?? '';
        const payerFullName = `${givenName} ${surname}`.trim() || null;
        const shippingData = authorizedEvent.purchase_units?.[0]?.shipping ?? {};
        const shippingAddress = shippingData.address ?? {};

        expressCheckoutContext.payPalCustomerData = {
            email_address: authorizedEvent.payer?.email_address,
            full_name: shippingData.name?.full_name ?? payerFullName,
            address_line_1: shippingAddress.address_line_1,
            address_line_2: shippingAddress.address_line_2 ?? '',
            country_code: shippingAddress.country_code,
            admin_area_2: shippingAddress.admin_area_2,
            postal_code: shippingAddress.postal_code,
            national_number: authorizedEvent.payer?.phone?.phone_number?.national_number ?? '',
            admin_area_1: shippingAddress.admin_area_1 ?? ''
        };
        expressCheckoutContext.googlePayCustomerData = null;
        expressCheckoutContext.applePayCustomerData = null;

        actions.resolve();
    }
});

/**
 * @returns {Promise<import('@adyen/adyen-web').GooglePay|void>}
 */
const mountGooglePay = (checkout, expressCheckoutContext, container) => {
    const googlePayComponent = new GooglePay(checkout, createGooglePayConfiguration(expressCheckoutContext));
    container.adyenComponent = googlePayComponent;
    return googlePayComponent.isAvailable()
        .then(() => googlePayComponent.mount(container))
        .catch((error) => console.error('Google Pay is not available', error));
};

/**
 * @returns {Promise<import('@adyen/adyen-web').ApplePay|void>}
 */
const mountApplePay = (checkout, expressCheckoutContext, container) => {
    const applePayComponent = new ApplePay(checkout, createApplePayConfiguration(expressCheckoutContext));
    container.adyenComponent = applePayComponent;
    return applePayComponent.isAvailable()
        .then(() => applePayComponent.mount(container))
        .catch((error) => console.error('Apple Pay is not available', error));
};

/**
 * @returns {Promise<import('@adyen/adyen-web').PayPal|void>}
 */
const mountPayPal = (checkout, expressCheckoutContext, container) => {
    const payPalComponent = new PayPal(checkout, createPayPalConfiguration(expressCheckoutContext));
    container.adyenComponent = payPalComponent;
    return payPalComponent.isAvailable()
        .then(() => payPalComponent.mount(container))
        .catch((error) => console.error('PayPal is not available', error));
};

window.initExpressCheckout = async function () {
    if (window.expressCheckoutConfiguration === undefined || window.expressCheckoutConfiguration === null) {
        return;
    }

    const googlePayContainer = getExpressCheckoutContainer('#googlepay-container');
    const applePayContainer = getExpressCheckoutContainer('#applepay-container');
    const payPalContainer = getExpressCheckoutContainer('#paypal-container');
    if (!googlePayContainer && !applePayContainer && !payPalContainer) {
        return;
    }

    clearExpressCheckoutContainer(googlePayContainer);
    clearExpressCheckoutContainer(applePayContainer);
    clearExpressCheckoutContainer(payPalContainer);

    const expressCheckoutContext = {
        /** @type {(import('@adyen/adyen-web').google.payments.api.Address & {email: string}) | null} */
        googlePayCustomerData: null,
        /** @type {{emailAddress: string, givenName: string, familyName: string, addressLines: string[], countryCode: string, locality: string, postalCode: string, phoneNumber: string, administrativeArea: string}|null} */
        applePayCustomerData: null,
        /** @type {{email_address: string, full_name: string, address_line_1: string, address_line_2: string, country_code: string, admin_area_2: string, postal_code: string, national_number: string, admin_area_1: string}|null} */
        payPalCustomerData: null
    };
    const checkout = await AdyenCheckout(createAdyenConfiguration(expressCheckoutContext));

    if (googlePayContainer) {
        await mountGooglePay(checkout, expressCheckoutContext, googlePayContainer);
    }
    if (applePayContainer) {
        await mountApplePay(checkout, expressCheckoutContext, applePayContainer);
    }
    if (payPalContainer) {
        await mountPayPal(checkout, expressCheckoutContext, payPalContainer);
    }

    if (window.expressCheckoutConfiguration.redirectResult !== null) {
        checkout.submitDetails({
            details: {
                redirectResult: window.expressCheckoutConfiguration.redirectResult
            }
        });
    }
};

window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        window.initExpressCheckout();
    }
});

domready(function () {
    window.initExpressCheckout();
});